import React, { useState, useContext, useEffect } from 'react';
import { AppContext } from '../../state';
import { TimeIntervalSelect, DataError } from 'components/views';
import { Typography, Spin } from 'antd';
import { TopLossesChart } from './chart';
import { getHistogram } from '../state-time/utils/get-histogram';
import { getSummary, IStateTimeSummaryModel } from '../state-time/utils/get-summary';
import { applyShiftSchedule } from '../state-time/utils/apply-shift-schedule';
import { IStateTimeModel } from '__core/models';

export const TopLossesView = () => {

    const { state, dispatch } = useContext(AppContext);
    const [summary, setSummary] = useState<IStateTimeSummaryModel | null>(null);
    const [error, setError] = useState(false);

    const customer_id = state.customer?.id || '';
    const job_id = state.job?.id || '';
    const timeZone = state.job?.time_zone || 'America/Chicago';
    const schedule = state.schedule;

    useEffect(() => {
        let cancel = false;
        setSummary(null);
        setError(false);
        (async () => {
            try {
                const newDataBuffer: IStateTimeModel[][] = [];
                let count = 100000;
                let startTime = state.timeIntervalSelect.startTime;
                let tryCount = 0;
                while (count >= 100000 && tryCount <= 20) {
                    const readData = await state.api.read.stateTime.list({
                        customer_id,
                        job_id,
                        startTime,
                        endTime: state.timeIntervalSelect.endTime,
                        refresh: state.timeIntervalSelect.endTime > Date.now() / 1000,
                    });
                    count = readData.length;
                    tryCount++;
                    newDataBuffer.push(readData);
                    if (readData.length) startTime = readData[readData.length - 1].time + 1;
                }
                const newDataBufferFiltered = newDataBuffer.filter(data => data.length);
                newDataBufferFiltered.sort((a, b) => a[0].time - b[0].time);
                const newData = newDataBufferFiltered.reduce<IStateTimeModel[]>((a, c) => {
                    if (!a.length) return c;
                    if (!c.length) return a;
                    if (a[a.length - 1].state === c[0].state) return [...a, ...c.slice(1)]
                    return [...a, ...c];
                }, []);
                if (!cancel) {
                    const scheduleFilteredData = schedule?.id && state.timeIntervalSelect.shiftName
                        ? applyShiftSchedule({
                            startTime: state.timeIntervalSelect.startTime,
                            endTime: state.timeIntervalSelect.endTime,
                            data: newData,
                            schedule,
                            shiftName: state.timeIntervalSelect.shiftName,
                            timeZone
                        })
                        : newData;
                    const newHistogram = getHistogram({ data: scheduleFilteredData, startTime: state.timeIntervalSelect.startTime, endTime: state.timeIntervalSelect.endTime, hideOutliers: state.timeIntervalSelect.hideOutliers });
                    const newSummary = getSummary({ histogram: newHistogram, timeZone })
                    setSummary(newSummary);
                }
            } catch (e: any) {
                setError(true);
            }
        })();
        return () => { cancel = true };
    }, [customer_id, job_id, state.timeIntervalSelect, setSummary]);


    return (
        <div>
            <div>
                <Typography.Title>Top Losses</Typography.Title>
                <div
                    style={{
                        position: "sticky",
                        top: 0,
                        left: 0,
                        zIndex: 99,
                        backgroundColor: "#f7f7f7",
                        borderRadius: 5,
                    }}
                >
                    <TimeIntervalSelect
                        data={state.timeIntervalSelect}
                        onChange={timeIntervalSelect => dispatch({ type: 'set-time-interval', timeIntervalSelect })}
                        timeZone={timeZone}
                        shiftList={(schedule?.shifts || []).map(({ name }) => name)}
                        wrap={state.isMobile} />
                </div>
            </div>
            {error
                ? <DataError />
                : !summary
                    ? <Spin tip='Loading...' size='large' />
                    : <>
                        <TopLossesChart
                            summary={summary}
                            jobConfig={state.config}
                            timeZone={timeZone}
                            mobile={state.isMobile} />
                    </>
            }

        </div>
    )
}
